import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useActiveBillableTimerState } from '../../redux/reducers/activeBillableTimerReducer';
import { TimeTrackingEntry } from '../types';

const MS_PER_HOUR = 1000 * 60 * 60;
const TICK_INTERVAL_MS = 1000;

/** Formats decimal hours as H:MM:SS. */
export const formatHoursAsClock = (hours: number) => {
    const totalSeconds = Math.max(0, Math.floor(hours * 3600));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

export type ActiveTimerContextType = {
    activeTimeEntry?: TimeTrackingEntry;
    isRunning: boolean;
    elapsedMs: number;
    elapsedHours: number;
    formatted: string;
    isActiveForEntry: (entryId?: string) => boolean;
    isActiveForTask: (taskId?: string) => boolean;
    hoursForEntry: (entry?: TimeTrackingEntry) => number;
};

const ActiveTimerContext = createContext<ActiveTimerContextType | undefined>(undefined);

/**
 * Owns the single ticking clock for the running time entry so every timer display
 * derives the same elapsed value from the same redux state.
 */
export const ActiveTimerProvider = (props: { children: ReactNode }) => {
    const { activeTimeEntry } = useActiveBillableTimerState();

    const startedAtMs = activeTimeEntry?.startTime ? new Date(activeTimeEntry.startTime).getTime() : 0;
    const isRunning = !!activeTimeEntry?.timerOn && !!startedAtMs;

    const [nowMs, setNowMs] = useState<number>(() => Date.now());

    useEffect(() => {
        if (!isRunning) {
            return;
        }

        setNowMs(Date.now());
        const intervalId = window.setInterval(() => setNowMs(Date.now()), TICK_INTERVAL_MS);

        return () => window.clearInterval(intervalId);
    }, [isRunning, startedAtMs]);

    const value = useMemo<ActiveTimerContextType>(() => {
        const elapsedMs = isRunning ? Math.max(0, nowMs - startedAtMs) : 0;
        const elapsedHours = (activeTimeEntry?.hours ?? 0) + elapsedMs / MS_PER_HOUR;

        const isActiveForEntry = (entryId?: string) => !!entryId && activeTimeEntry?.entryId === entryId;
        const isActiveForTask = (taskId?: string) => !!taskId && activeTimeEntry?.taskId === taskId;
        const hoursForEntry = (entry?: TimeTrackingEntry) => {
            if (!entry) return 0;
            return isRunning && isActiveForEntry(entry.entryId) ? elapsedHours : entry.hours ?? 0;
        };

        return {
            activeTimeEntry,
            isRunning,
            elapsedMs,
            elapsedHours,
            formatted: formatHoursAsClock(elapsedHours),
            isActiveForEntry,
            isActiveForTask,
            hoursForEntry,
        };
    }, [activeTimeEntry, isRunning, nowMs, startedAtMs]);

    return <ActiveTimerContext.Provider value={value}>{props.children}</ActiveTimerContext.Provider>;
};

export const useActiveTimer = (): ActiveTimerContextType => {
    const context = useContext(ActiveTimerContext);
    if (!context) {
        throw new Error('useActiveTimer must be used within an ActiveTimerProvider');
    }
    return context;
};
